import { useEffect, useState } from 'react'
import useSWR from 'swr'
import { authHelper, getPcekZone } from '@entities/user'
import { ewalletReqHeaders } from '@shared/config/constants'
import { ewalletApi } from '@root/api/api'
import { OperationDetailsRq, OperationDetailsRs, TransactionResponse } from '@root/api/ump-ewallet'
import { OperationStatusType } from '@root/types/generated'

interface OperationDetailsParams {
  userId: string
  mdOrder: string
}

const fetchOperationDetails = async (params: OperationDetailsRq) => {
  const { userId, mdOrder } = params as OperationDetailsParams
  const isAuth = authHelper.isAuthenticated()

  const response = await ewalletApi.getOperationDetailsDo(getPcekZone(), userId, mdOrder, {
    headers: {
      ...ewalletReqHeaders,
      ...(isAuth ? { Authorization: authHelper.getAuthHeader() } : {}),
      fhprequestid: window?.gibRequestId,
      fhpsessionid: window?.gibSessionId,
    },
  })

  return response.data
}

const useOperationDetails = (params: OperationDetailsRq, error3ds: TransactionResponse) => {
  const [gibData, setGibData] = useState<{ requestId?: string; sessionId?: string }>({})

  useEffect(() => {
    if (typeof window === 'undefined') return

    const checkGibData = () => {
      const requestId = window.gibRequestId
      const sessionId = window.gibSessionId

      if (requestId && sessionId) {
        setGibData({ requestId, sessionId })
        clearInterval(interval)
      }
    }

    checkGibData()

    const interval = setInterval(checkGibData, 1000)

    return () => clearInterval(interval)
  }, [])

  const swrKey =
    gibData.requestId && gibData.sessionId
      ? ['ewallet/getOperationDetails', params.mdOrder, gibData.requestId, gibData.sessionId]
      : null

  const { data, error, isValidating, mutate } = useSWR<OperationDetailsRs, Error>(
    swrKey,
    () => fetchOperationDetails(params),
    {
      refreshInterval: (data) => {
        if (
          !error3ds &&
          data &&
          (data.state === OperationStatusType.WAITING_CONFIRMATION || data.state === OperationStatusType.IN_PROGRESS)
        ) {
          return 5000
        }

        return 0
      },
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    }
  )

  return {
    data,
    isLoading: isValidating,
    isError: error,
    inProgress: isValidating,
    mutate,
  }
}

export { useOperationDetails }
